import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import {
  DeleteTemplateItemDocument,
  GetTemplateItemsDocument,
  TemplateItemDataFragment,
} from '../../../../data/types/api/graphql';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';

type TemplateDeletePromptProps = {
  templateItem: TemplateItemDataFragment;
  onDeleted?: (value: TemplateItemDataFragment) => void;
  onClose?: () => void;
};

export function TemplateDeletePrompt({
  templateItem,
  onDeleted,
  onClose,
}: TemplateDeletePromptProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const [deleteTemplateItem] = useMutation(DeleteTemplateItemDocument, {
    refetchQueries: [GetTemplateItemsDocument],
  });

  const onHandleDelete = useCallback(async () => {
    try {
      const result = await deleteTemplateItem({
        variables: {
          id: templateItem?.id ?? '',
        },
      });

      if (result.data?.templateDelete) {
        showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: intl.formatMessage({
            id: 'template.template-delete.success',
          }),
        });

        onDeleted?.(templateItem);

        onClose?.();
      }
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    }
  }, [
    intl,
    templateItem,
    deleteTemplateItem,
    showAlertMessage,
    onDeleted,
    onClose,
  ]);

  if (!templateItem) return null;

  return (
    <ModalPrompt
      testId="template-delete"
      onConfirm={onHandleDelete}
      onClose={onClose}
    >
      <p>
        <CustomFormattedMessage id="template.template-delete.content" />
      </p>
    </ModalPrompt>
  );
}
